/**
 * Presentation helpers shared by the tree and the modals.
 *
 * These describe how state is *shown*. They never decide whether a flag is on — that is
 * resolved server-side and depends on a school, which this screen has not named.
 */

export const RELEASE_STATUS = ['OFF', 'PARTIAL', 'CANARY', 'ALL'];
export const AVAILABILITY = ['LIVE', 'KILLED', 'MAINTENANCE'];

export const severityColour = (severity) => {
  switch (severity) {
    case 'success':
      return { bg: '#e6f4ea', fg: '#1e7b34' };
    case 'info':
      return { bg: '#e8f0fe', fg: '#1a53b0' };
    case 'warning':
      return { bg: '#fef7e0', fg: '#8a6100' };
    case 'error':
      return { bg: '#fce8e6', fg: '#b3261e' };
    default:
      return { bg: '#f1f3f4', fg: '#5f6368' };
  }
};

export const releaseColour = (status) => {
  switch (status) {
    case 'ALL':
      return { bg: '#e6f4ea', fg: '#1e7b34' };
    case 'CANARY':
      return { bg: '#fef7e0', fg: '#8a6100' };
    case 'PARTIAL':
      return { bg: '#e8f0fe', fg: '#1a53b0' };
    default:
      return { bg: '#f1f3f4', fg: '#5f6368' };
  }
};

export const availabilityColour = (availability) => {
  switch (availability) {
    case 'KILLED':
      return { bg: '#fce8e6', fg: '#b3261e' };
    case 'MAINTENANCE':
      return { bg: '#fef7e0', fg: '#8a6100' };
    default:
      return null; // LIVE is the normal case and gets no badge — badges are for exceptions.
  }
};

/** Friendlier names than the raw UserType values the API returns. */
export const ECOSYSTEM_LABELS = {
  AKADEMY: 'Akademy',
  BUZZ_ONE: 'BuzzOne',
  BCC: 'BCC',
  LEAD: 'Discovery',
  SCHOOL: 'School',
  ATHENEUM: 'Atheneum',
  TEENYBEANS: 'Teenybeans',
  BIP: 'BIP',
};

export const ecosystemLabel = (value) => ECOSYSTEM_LABELS[value] || value;

/**
 * A node's owning product.
 *
 * The **catalog** is authoritative, not the database row. Row creation is insert-only by
 * design, so a flag tagged in `flag.catalog.ts` after its row already existed keeps
 * `ecosystem: undefined` on that row forever. Reading the row first would mean catalog
 * edits silently never take effect — which is exactly the bug that made this toggle
 * vanish. The row value stays as a fallback for orphans, which have no catalog entry.
 */
export const nodeEcosystem = (node) => {
  const meta = node && node.node && node.node.meta;
  const row = node && node.node && node.node.row;
  return (meta && meta.ecosystem) || (row && row.ecosystem) || undefined;
};

/** Every ecosystem that actually has a flag, so the toggle never offers an empty tab. */
export const collectEcosystems = (nodes) => {
  const found = new Set();
  const walk = (list) => {
    (list || []).forEach((node) => {
      const eco = nodeEcosystem(node);
      if (eco) found.add(eco);
      walk(node.children);
    });
  };
  walk(nodes);
  return Array.from(found).sort();
};

/**
 * Narrows the tree to one product for display.
 *
 * Two rules keep this honest rather than merely tidy:
 *
 * 1. A node survives if it matches, **or if any descendant matches**. Containers carry no
 *    ecosystem of their own — they are structural — so filtering them out naively would
 *    delete the scaffolding and orphan every flag beneath it.
 *
 * 2. Flags with no ecosystem survive every filter. Those are cross-cutting infrastructure,
 *    typically kill switches on something several products share, and hiding them from all
 *    filters is the opposite of what you want mid-incident.
 *
 * Note this filters *display only*. The tree arrives already carrying gating computed from
 * every row, so a badge still correctly reads "Blocked by attendance" even when the
 * blocking ancestor is not on screen — which is exactly why the backend refuses to build
 * a filtered tree.
 */
export const filterTreeByEcosystem = (nodes, ecosystem) => {
  // No selection only happens in the moment before the first tree resolves a default.
  if (!ecosystem) return nodes;

  const prune = (list) =>
    (list || []).reduce((kept, node) => {
      const children = prune(node.children);
      const own = nodeEcosystem(node);
      const matches = !own || own === ecosystem;
      if (matches || children.length > 0) {
        kept.push({ ...node, children });
      }
      return kept;
    }, []);

  return prune(nodes);
};

/** Environments are lowercase in the API; only their casing changes for display. */
export const environmentLabel = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Unknown environment';

/**
 * What a peer environment did, in words.
 *
 * `SKIPPED` and `FAILED` stay apart. Skipped means no peer URL is configured, so the
 * environment was never contacted — the normal case locally and in CI. Failed means it was
 * contacted and did not do the work, which means the environments have diverged. Merging
 * them into one "not synced" state would hide a real production divergence behind the most
 * common harmless one.
 */
export const PEER_STATUS_META = {
  APPLIED: {
    label: 'Applied',
    severity: 'success',
    note: 'Rows were created in this environment.',
  },
  DRY_RUN: {
    label: 'Preview only',
    severity: 'info',
    note: 'Nothing was written here — this is what applying would do.',
  },
  SKIPPED: {
    label: 'Not contacted',
    severity: 'neutral',
    note:
      'No peer URL is configured for this environment, so it was never called. Expected on local and CI.',
  },
  FAILED: {
    label: 'Failed',
    severity: 'error',
    note:
      'This environment was contacted and did not do the work, so it has diverged from the one you called. Re-running the sync is the fix — the operation is idempotent.',
  },
};

export const peerStatusMeta = (status) =>
  PEER_STATUS_META[status] || {
    label: status || 'Unknown',
    severity: 'neutral',
    note: 'Unrecognised status — treat as not synced and re-run.',
  };

/** Every key with a row in this environment, so we can tell "missing" from "already there". */
export const treeKeys = (nodes) => {
  const keys = new Set();
  const walk = (list) => {
    (list || []).forEach((node) => {
      const row = node.node && node.node.row;
      if (row && row.key) keys.add(row.key);
      walk(node.children);
    });
  };
  walk(nodes);
  return keys;
};

/**
 * The CONTAINER ancestors a create would bring into existence alongside the flag itself.
 *
 * The backend creates these implicitly from the key's prefixes, so a create is rarely one
 * row. Naming them up front is the difference between "I added a flag" and being surprised
 * by a new namespace node that can take everything beneath it down.
 */
export const impliedContainers = (key, existingKeys) => {
  const parts = (key || '').trim().split('.').filter(Boolean);
  const implied = [];
  for (let i = 1; i < parts.length; i += 1) {
    const ancestor = parts.slice(0, i).join('.');
    if (!existingKeys || !existingKeys.has(ancestor)) implied.push(ancestor);
  }
  return implied;
};

/**
 * Keys a dry-run sync says are declared in the catalog but missing from this environment.
 *
 * `GET feature-flags` returns rows, not catalog entries, so this plan is the only way to
 * learn what is creatable. Containers are dropped: they are derived from the keys above,
 * never chosen, and creating the leaf brings them along anyway.
 */
export const creatableKeysFromPlan = (result) => {
  const created = (result && result.local && result.local.created) || [];
  return created.filter((row) => row.kind !== 'CONTAINER').map((row) => row.key);
};

/** Human summary of who a flag reaches, given only its own row. */
export const audienceSummary = (row) => {
  switch (row.releaseStatus) {
    case 'ALL':
      return 'Everyone';
    case 'PARTIAL':
      return `${(row.schoolIds && row.schoolIds.length) || 0} school(s)`;
    case 'CANARY':
      return `${row.rolloutPct || 0}% of schools`;
    default:
      return 'Nobody';
  }
};

/**
 * Why a node is gated by something above it, in words.
 *
 * Two distinct fields, kept distinct deliberately. `blockedByAncestor` means an ancestor
 * is off or not live, so nobody sees this regardless of its own setting.
 * `narrowedByAncestor` means an ancestor is on a pilot or canary, so this reaches at most
 * that ancestor's audience. Collapsing them into one "blocked" badge would claim a child
 * is dark when it is merely limited; dropping the second would show a child as fully
 * released while its parent sits at 10%.
 */
export const gateNotices = (node) => {
  const notices = [];
  if (node.blockedByAncestor) {
    notices.push({
      severity: 'blocked',
      text: `Blocked by ${node.blockedByAncestor}`,
    });
  }
  if (node.narrowedByAncestor) {
    notices.push({
      severity: 'narrowed',
      text: `Limited by ${node.narrowedByAncestor}`,
    });
  }
  return notices;
};
